import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth, type AuthContextValue } from '@finmin/auth';
import { Spinner } from '@finmin/ui';
import '@finmin/ui/styles.css';
import { routeTree } from './routeTree.gen';
import { apiClient, tokenStore } from './lib/api';
import { trackPageView } from './lib/analytics';
import { queryClient } from './api/query-client';

// `auth` is a placeholder — the live value is injected at render time via
// <RouterProvider context={...} /> inside <RoutedApp /> below. We deliberately
// don't mount the router until auth status has resolved (see RoutedApp), so
// `beforeLoad` guards never see `status === 'loading'`.
const router = createRouter({
  routeTree,
  context: { auth: undefined!, queryClient },
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// GA4 page views. index.html sets send_page_view:false, so this is the ONLY
// source of page_view events — it covers the first load and every subsequent
// client-side navigation. `pathChanged` filters out pure hash/search updates
// that would otherwise double-count a single page.
router.subscribe('onResolved', ({ toLocation, pathChanged }) => {
  if (pathChanged) trackPageView(toLocation.pathname);
});

function RoutedApp() {
  const auth = useAuth();
  if (auth.status === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  const ctx: { auth: AuthContextValue } = { auth };
  return <RouterProvider router={router} context={ctx} />;
}
const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Missing #root element');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider client={apiClient} onClear={() => tokenStore.clear()}>
        <RoutedApp />
      </AuthProvider>
      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
      )}
    </QueryClientProvider>
  </StrictMode>,
);
